// @docs:
//   - docs/core/operations/ROUTING_SERVICE.md
//
// Admin › Routing — pick the passage on a map: choose a basemap, click to place the start,
// the destination and any waypoints, drag them to adjust.

import { Marker, Polyline, TileLayer, Tooltip, useMapEvents } from 'react-leaflet';
import { AdminFleetMap } from '../../components/admin/console/AdminFleetMap';
import { BASEMAPS, SEAMARKS, basemapById, cleanPoint, passageLine } from './routingMap';
import type { LatLon } from '../../services/api';

export type PickMode = 'start' | 'end' | 'via' | null;

export interface Passage {
  start: LatLon | null;
  end: LatLon | null;
  via: LatLon[];
}

interface Props {
  passage: Passage;
  onChange: (p: Passage) => void;
  mode: PickMode;
  onMode: (m: PickMode) => void;
  basemap: string;
  onBasemap: (id: string) => void;
  seamarks: boolean;
  onSeamarks: (on: boolean) => void;
}

function ClickToPlace({ passage, onChange, mode, onMode }: Pick<Props, 'passage' | 'onChange' | 'mode' | 'onMode'>) {
  useMapEvents({
    click(e) {
      if (!mode) return;
      const p = cleanPoint(e.latlng.lat, e.latlng.lng);
      if (mode === 'start') {
        onChange({ ...passage, start: p });
        onMode(passage.end ? null : 'end');
      } else if (mode === 'end') {
        onChange({ ...passage, end: p });
        onMode(null);
      } else {
        onChange({ ...passage, via: [...passage.via, p] });
      }
    },
  });
  return null;
}

const button = (on: boolean) => `px-3 py-1.5 rounded-lg text-[13px] font-semibold border ${on ? 'bg-cyan-600 border-cyan-500 text-white' : 'border-slate-600 text-slate-300 hover:bg-slate-700'}`;

export function RoutePicker({ passage, onChange, mode, onMode, basemap, onBasemap, seamarks, onSeamarks }: Props) {
  const base = basemapById(basemap);
  const points = [passage.start, ...passage.via, passage.end].filter((p): p is LatLon => !!p);
  const line = passageLine(points);
  const centre: LatLon = points.length ? [points.reduce((s, p) => s + p[0], 0) / points.length, line.reduce((s, p) => s + p[1], 0) / line.length] : [-28, 170];
  const drag = (set: (p: LatLon) => void) => ({ dragend: (e: { target: { getLatLng(): { lat: number; lng: number } } }) => { const ll = e.target.getLatLng(); set(cleanPoint(ll.lat, ll.lng)); } });
  const hint = mode === 'start' ? 'Click the map where the passage starts.' : mode === 'end' ? 'Click the map where the passage ends.' : mode === 'via' ? 'Click to add waypoints in order; press Done when finished.' : 'Drag a marker to move it, or choose what to place.';

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" className={button(mode === 'start')} onClick={() => onMode(mode === 'start' ? null : 'start')}>Place start</button>
        <button type="button" className={button(mode === 'end')} onClick={() => onMode(mode === 'end' ? null : 'end')}>Place destination</button>
        <button type="button" className={button(mode === 'via')} onClick={() => onMode(mode === 'via' ? null : 'via')}>{mode === 'via' ? 'Done' : 'Add waypoints'}</button>
        {passage.via.length > 0 && (
          <button type="button" className={button(false)} onClick={() => onChange({ ...passage, via: [] })}>Clear {passage.via.length} waypoint{passage.via.length > 1 ? 's' : ''}</button>
        )}
        <span className="flex-1" />
        <label className="flex items-center gap-2 text-[13px] text-slate-300">
          Basemap
          <select className="rounded-lg bg-slate-900 border border-slate-600 px-2 py-1.5 text-[13px] text-white" value={base.id} onChange={(e) => onBasemap(e.target.value)}>
            {BASEMAPS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-[13px] text-slate-300">
          <input type="checkbox" checked={seamarks} onChange={(e) => onSeamarks(e.target.checked)} /> Seamarks
        </label>
      </div>
      <p className="text-[13px] text-slate-400">{hint}</p>
      <div className={`h-[460px] rounded-lg overflow-hidden border ${mode ? 'border-cyan-500' : 'border-slate-700'}`} style={{ cursor: mode ? 'crosshair' : undefined }}>
        <AdminFleetMap center={centre} zoom={points.length ? 5 : 4} tileUrl={base.url} tileSource={base.source} tileAttribution={base.attribution} tileMaxZoom={base.maxZoom}>
          {seamarks && <TileLayer url={SEAMARKS.url} attribution={SEAMARKS.attribution} maxZoom={SEAMARKS.maxZoom} maxNativeZoom={SEAMARKS.maxNativeZoom} />}
          <ClickToPlace passage={passage} onChange={onChange} mode={mode} onMode={onMode} />
          {line.length > 1 && <Polyline positions={line} pathOptions={{ color: '#22d3ee', weight: 2, dashArray: '6 6' }} />}
          {passage.start && (
            <Marker position={passage.start} draggable eventHandlers={drag((p) => onChange({ ...passage, start: p }))}>
              <Tooltip permanent direction="top">Start</Tooltip>
            </Marker>
          )}
          {passage.via.map((v, i) => (
            <Marker key={`${i}:${v[0]},${v[1]}`} position={v} draggable eventHandlers={drag((p) => onChange({ ...passage, via: passage.via.map((x, k) => (k === i ? p : x)) }))}>
              <Tooltip permanent direction="top">{i + 1}</Tooltip>
            </Marker>
          ))}
          {passage.end && (
            <Marker position={passage.end} draggable eventHandlers={drag((p) => onChange({ ...passage, end: p }))}>
              <Tooltip permanent direction="top">Destination</Tooltip>
            </Marker>
          )}
        </AdminFleetMap>
      </div>
    </div>
  );
}
